/**
 * ClientGoalsCard — the per-client Goals card on the dashboards tab
 * (Story 18.3 AC#6).
 *
 * Lists a client's goals (via useClientGoals — the only data path) with a
 * progress bar colored by the FRESH `progress.derivedStatus` (display truth,
 * never the stored `status` column except when paused), a current/target
 * value line formatted by unit/currency (the shared formatGoalValue), and a
 * pace line. Add/edit open the centered GoalFormDialog (client-locked mode);
 * delete uses a §4.4 confirmation Dialog (snapshots cascade server-side).
 *
 * Card chrome per §7 (rounded-[22px], 0.5px inline border, shadow-xs); header
 * Phosphor Target icon + outline "Add goal" button (§2). Phosphor icons only.
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@provenleap/ui";
import {
  Target,
  Flag,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import {
  useClientGoals,
  useDeleteClientGoal,
} from "../../hooks/useClientGoals";
import { GoalFormDialog } from "./GoalFormDialog";
import { formatGoalValue, clampBarFill } from "../../lib/goalFormat";
import { platformBadge } from "../../lib/integrations";
import {
  displayStatus,
  type GoalListItem,
  type GoalStatus,
} from "../../lib/goalPageDisplay";

interface ClientGoalsCardProps {
  clientId: string;
}

const CARD_BORDER = "0.5px solid var(--hairline)";

// AC#6: bar color by derivedStatus — green complete / blue on_track /
// red off_track (GoalWidget STATUS_COLORS semantics; paused = muted). This is
// the DB status space, NOT goalPageDisplay's GOAL_STATUS_BADGES (whose
// on_track is green) — the card deliberately mirrors the report widget.
const STATUS_BAR: Record<GoalStatus, string> = {
  complete: "bg-green-500",
  on_track: "bg-blue-600",
  off_track: "bg-red-500",
  paused: "bg-muted-foreground/40",
};

function paceLine(goal: GoalListItem): string {
  const { progress, unit, currencyCode } = goal;
  const projected = formatGoalValue(progress.projectedValue, unit, currencyCode);
  const target = formatGoalValue(goal.targetValue, unit, currencyCode);
  if (displayStatus(goal) === "complete") {
    return "Goal reached";
  }
  return progress.onPace
    ? `On pace — projected ${projected} by end of period`
    : `Behind pace — projected ${projected} of ${target}`;
}

function GoalRow({
  goal,
  onEdit,
  onDelete,
}: {
  goal: GoalListItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const status = displayStatus(goal);
  const badge = goal.platform ? platformBadge(goal.platform) : null;
  const currentFormatted = formatGoalValue(
    goal.currentValue,
    goal.unit,
    goal.currencyCode,
  );
  const targetFormatted = formatGoalValue(
    goal.targetValue,
    goal.unit,
    goal.currencyCode,
  );
  const percent = goal.progress.percentage;
  const periodEnded = goal.progress.daysRemaining <= 0;

  return (
    <div className="py-3" data-testid="goal-row">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {badge && (
            <img
              src={badge.logoSrc}
              alt=""
              title={badge.displayName}
              className="h-4 w-4 shrink-0 rounded-sm"
            />
          )}
          <span className="truncate text-sm font-medium text-foreground">
            {goal.name}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label={`Edit ${goal.name}`}
            onClick={onEdit}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
          >
            <PencilSimple size={14} />
          </button>
          <button
            type="button"
            aria-label={`Delete ${goal.name}`}
            onClick={onDelete}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive transition-colors cursor-pointer"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>

      {/* Progress bar (fill clamped 0–100, target tick at 100%). */}
      <div
        className="relative mt-2 h-2 w-full rounded-full bg-muted"
        aria-hidden="true"
      >
        <div
          className={`h-full rounded-full transition-all duration-500 ${STATUS_BAR[status]}`}
          style={{ width: `${clampBarFill(percent)}%` }}
        />
        <div className="absolute right-0 top-0 h-full w-0.5 bg-muted-foreground/40" />
      </div>

      {/* Value + percentage. */}
      <div className="mt-1.5 flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-semibold text-foreground">
          {currentFormatted} / {targetFormatted}
        </span>
        <span className="text-[12px] text-muted-foreground">
          {percent.toFixed(0)}%
        </span>
      </div>

      {/* Pace line + days remaining. */}
      <p className="mt-0.5 text-[12px] text-muted-foreground">{paceLine(goal)}</p>
      {!periodEnded && (
        <p className="text-[12px] text-muted-foreground/80">
          {goal.progress.daysRemaining} days remaining
        </p>
      )}
    </div>
  );
}

export function ClientGoalsCard({ clientId }: ClientGoalsCardProps) {
  const { data: goals, isLoading } = useClientGoals(clientId);
  const deleteGoal = useDeleteClientGoal(clientId);

  const [createOpen, setCreateOpen] = useState(false);
  const [editGoal, setEditGoal] = useState<GoalListItem | null>(null);
  const [lastEditGoal, setLastEditGoal] = useState<GoalListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GoalListItem | null>(null);
  const [lastDeleteTarget, setLastDeleteTarget] = useState<GoalListItem | null>(null);

  // Keep the last non-null goal alive ~300ms so the dialog exit animation can
  // play (§4.6 — never conditionally unmount the modal for non-null props).
  useEffect(() => {
    if (editGoal) {
      setLastEditGoal(editGoal);
      return;
    }
    if (!lastEditGoal) return;
    const t = setTimeout(() => setLastEditGoal(null), 300);
    return () => clearTimeout(t);
  }, [editGoal, lastEditGoal]);

  useEffect(() => {
    if (deleteTarget) {
      setLastDeleteTarget(deleteTarget);
      return;
    }
    if (!lastDeleteTarget) return;
    const t = setTimeout(() => setLastDeleteTarget(null), 300);
    return () => clearTimeout(t);
  }, [deleteTarget, lastDeleteTarget]);

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteGoal.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <div
      className="!rounded-[22px] bg-background p-5 shadow-xs"
      style={{ border: CARD_BORDER }}
      data-testid="client-goals-card"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target size={18} className="text-foreground" />
          {/* text-lg matches the sibling RecentReportsCard CardTitle on this
              same client page — keep card headers visually identical. */}
          <h3 className="text-lg font-semibold text-foreground">Goals</h3>
        </div>
        <Button
          variant="outline"
          className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
          style={{ border: CARD_BORDER }}
          onClick={() => setCreateOpen(true)}
        >
          <Target size={16} className="mr-1.5" />
          Create Goal
        </Button>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="space-y-4" data-testid="goals-loading">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
                <div className="h-2 w-full animate-pulse rounded-full bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : !goals || goals.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-10 text-center"
            data-testid="goals-empty"
          >
            <div className="mb-3 rounded-full bg-muted p-3">
              <Flag size={28} className="text-muted-foreground" />
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              No goals yet — set a target to start tracking progress
            </p>
            <Button
              variant="outline"
              className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
              style={{ border: CARD_BORDER }}
              onClick={() => setCreateOpen(true)}
            >
              <Target size={16} className="mr-1.5" />
              Create Goal
            </Button>
          </div>
        ) : (
          <div className="divide-y-[0.5px] divide-input/70">
            <AnimatePresence initial={false}>
              {goals.map((goal, index) => (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.03 * (index + 1) }}
                >
                  <GoalRow
                    goal={goal}
                    onEdit={() => setEditGoal(goal)}
                    onDelete={() => setDeleteTarget(goal)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Create — always-mounted (§4.6). */}
      <GoalFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        clientId={clientId}
      />

      {/* Edit — lingers via lastEditGoal so the exit animation plays. */}
      {lastEditGoal && (
        <GoalFormDialog
          open={!!editGoal}
          onOpenChange={(o) => !o && setEditGoal(null)}
          clientId={clientId}
          goal={lastEditGoal}
        />
      )}

      {/* Delete confirmation (§4.4). */}
      {lastDeleteTarget && (
        <DeleteGoalDialog
          open={!!deleteTarget}
          goalName={lastDeleteTarget.name}
          pending={deleteGoal.isPending}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

function DeleteGoalDialog({
  open,
  goalName,
  pending,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  goalName: string;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  function handleOpenChange(next: boolean) {
    if (!next && pending) return;
    onOpenChange(next);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delete goal?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {goalName} — its history will be removed. This cannot be undone.
                  </DialogPrimitive.Description>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: CARD_BORDER }}
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    disabled={pending}
                    onClick={onConfirm}
                  >
                    {pending ? "Deleting…" : "Delete"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
